import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import { getMediaUrl } from "../services/api";
import AdminLayout from "./AdminLayout";

function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [files, setFiles] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [form, setForm] = useState({
    title: "",
    image_url: "",
    link_url: "",
    sort_order: 1,
    is_active: true,
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const previews = useMemo(
    () => files.map((file) => ({ name: file.name, url: URL.createObjectURL(file) })),
    [files]
  );

  useEffect(() => () => previews.forEach((item) => URL.revokeObjectURL(item.url)), [previews]);

  const loadBanners = async () => {
    const res = await API.get("/admin/banners");
    setBanners(Array.isArray(res.data) ? res.data : []);
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveBanner = async () => {
    if (!files.length && !form.image_url) {
      alert("Upload image or enter banner image URL");
      return;
    }

    const payload = new FormData();
    payload.append("title", form.title);
    payload.append("image_url", form.image_url);
    payload.append("link_url", form.link_url);
    payload.append("sort_order", form.sort_order);
    payload.append("is_active", String(form.is_active));
    files.forEach((file) => payload.append("images", file));

    await API.post("/admin/banners", payload, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    setForm({ title: "", image_url: "", link_url: "", sort_order: 1, is_active: true });
    setFiles([]);
    loadBanners();
  };

  const toggleBanner = async (banner) => {
    await API.put(`/admin/banners/${banner.id}`, {
      ...banner,
      is_active: !banner.is_active,
    });
    loadBanners();
  };

  const deleteBanner = async (id) => {
    await API.delete(`/admin/banners/${id}`);
    loadBanners();
  };

  const updateBannerField = (id, field, value) => {
    setBanners((current) =>
      current.map((banner) => (banner.id === id ? { ...banner, [field]: value } : banner))
    );
  };

  const saveBannerSettings = async (banner) => {
    setSavingId(banner.id);
    try {
      await API.put(`/admin/banners/${banner.id}`, {
        ...banner,
        sort_order: Number(banner.sort_order || 0),
        is_active: !!banner.is_active,
      });
      await loadBanners();
    } finally {
      setSavingId(null);
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h2 style={title}>Banner Manager</h2>
            <p style={subtitle}>Upload banners and set Banner No. 1, 2, 3 to control the slider order.</p>
          </div>
        </div>

        <div style={formCard}>
          <input style={input} placeholder="Banner title" value={form.title} onChange={(event) => updateField("title", event.target.value)} />
          <input style={input} placeholder="Image URL fallback" value={form.image_url} onChange={(event) => updateField("image_url", event.target.value)} />
          <input style={input} placeholder="Link URL optional" value={form.link_url} onChange={(event) => updateField("link_url", event.target.value)} />
          <input style={input} type="number" min="1" placeholder="Banner No. 1" value={form.sort_order} onChange={(event) => updateField("sort_order", Number(event.target.value))} />
          <label style={checkLabel}>
            <input type="checkbox" checked={form.is_active} onChange={(event) => updateField("is_active", event.target.checked)} />
            Active
          </label>
          <input style={fileInput} type="file" accept="image/*" multiple onChange={(event) => setFiles(Array.from(event.target.files || []))} />
          <button type="button" style={primaryButton} onClick={saveBanner}>Add Banner</button>
        </div>

        {previews.length > 0 && (
          <div style={previewGrid}>
            {previews.map((item) => (
              <article key={item.name} style={bannerCard}>
                <img src={item.url} alt={item.name} style={bannerImage} />
                <div style={bannerInfo}>
                  <strong>{item.name}</strong>
                </div>
              </article>
            ))}
          </div>
        )}

        <div style={grid}>
          {banners.map((banner) => (
            <article key={banner.id} style={bannerCard}>
              <img src={getMediaUrl(banner.image_url)} alt={banner.title} style={bannerImage} />
              <div style={bannerInfo}>
                <strong>{banner.title}</strong>
                <small>Banner No. {banner.sort_order || 0} - {banner.is_active ? "Active" : "Hidden"}</small>
                <label style={numberLabel}>
                  Banner No.
                  <input
                    style={smallNumberInput}
                    type="number"
                    min="1"
                    value={banner.sort_order || 0}
                    onChange={(event) => updateBannerField(banner.id, "sort_order", Number(event.target.value))}
                  />
                </label>
              </div>
              <div style={actions}>
                <button type="button" style={saveButton} onClick={() => saveBannerSettings(banner)} disabled={savingId === banner.id}>
                  {savingId === banner.id ? "Saving..." : "Save No."}
                </button>
                <button type="button" style={secondaryButton} onClick={() => toggleBanner(banner)}>
                  {banner.is_active ? "Hide" : "Show"}
                </button>
                <button type="button" style={dangerButton} onClick={() => deleteBanner(banner.id)}>Delete</button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </AdminLayout>
  );
}

const page = { color: "#0f172a", display: "grid", gap: "18px" };
const header = { marginBottom: "6px" };
const title = { margin: 0 };
const subtitle = { margin: "4px 0 0", color: "#64748b" };
const formCard = { display: "grid", gridTemplateColumns: "1fr 1.4fr 1fr 130px 90px 1fr 140px", gap: "10px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "16px" };
const input = { height: "42px", border: "1px solid #d1d5db", padding: "0 12px", borderRadius: "10px" };
const fileInput = { ...input, padding: "8px 12px", height: "42px" };
const checkLabel = { display: "flex", alignItems: "center", gap: "7px" };
const primaryButton = { border: 0, background: "#2563eb", color: "#fff", borderRadius: "10px", fontWeight: 800, cursor: "pointer" };
const previewGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "14px" };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "14px" };
const bannerCard = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", overflow: "hidden" };
const bannerImage = { width: "100%", aspectRatio: "2 / 1", objectFit: "cover", display: "block" };
const bannerInfo = { display: "grid", gap: "4px", padding: "12px" };
const numberLabel = { marginTop: "8px", display: "grid", gap: "5px", color: "#475569", fontSize: "12px", fontWeight: 800 };
const smallNumberInput = { height: "36px", border: "1px solid #d1d5db", borderRadius: "10px", padding: "0 10px", fontWeight: 800 };
const actions = { display: "flex", gap: "8px", padding: "0 12px 12px" };
const secondaryButton = { flex: 1, height: "36px", border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", borderRadius: "10px", cursor: "pointer" };
const saveButton = { ...secondaryButton, borderColor: "#bbf7d0", background: "#ecfdf5", color: "#15803d", fontWeight: 800 };
const dangerButton = { ...secondaryButton, borderColor: "#fecaca", background: "#fef2f2", color: "#dc2626" };

export default AdminBanners;
